import {
  WidgetType,
  type ActionCardData,
  type ObjectState,
  type UpdaterData,
  sectionTypes,
  SignatureData,
} from '@playbooks/action-cards-utils/src'
import StateManager from '../services/stateManager'

function getDifference(newData: ActionCardData, prevData?: ActionCardData) {
  if (!prevData) return null

  const newD = findById(newData)
  const prevD = findById(prevData)

  const diff = Object.keys(prevD).reduce((acc, currentKey) => {
    const newEntry = newD[currentKey]
    const prevEntry = prevD[currentKey]

    if (!newEntry || !prevEntry) {
      return acc
    }

    setFieldIfChanged(acc, currentKey, 'isOpened', prevEntry.isOpened, newEntry.isOpened)

    const newStates = newEntry.states
    const oldStates = prevEntry.states

    if (!StateManager.areStatesEqual((oldStates || []) as ObjectState[], (newStates || []) as ObjectState[])) {
      mergeDiff(acc, currentKey, { states: newStates })
    }

    setFieldIfChanged(acc, currentKey, 'isSectionMinimized', prevEntry.isSectionMinimized, newEntry.isSectionMinimized)
    setFieldIfChanged(acc, currentKey, 'isLocked', prevEntry.isLocked, newEntry.isLocked)

    if (Object.hasOwn(newEntry, 'value')) {
      const newValue = newEntry.value

      if (!areValuesIdentical(prevEntry.value, newValue)) {
        mergeDiff(acc, currentKey, { value: newValue as never })
      }
    }

    if (Object.hasOwn(newEntry, 'checked')) {
      const newValue = newEntry.checked
      const newFreeTextValue = newEntry.freeTextValue

      if (prevEntry.checked !== newValue || prevEntry.freeTextValue !== newFreeTextValue) {
        mergeDiff(acc, currentKey, { checked: newValue, freeTextValue: newFreeTextValue })
      }
    }

    setFieldIfChanged(acc, currentKey, 'updatedAt', prevEntry.updatedAt, newEntry.updatedAt)
    setFieldIfChanged(acc, currentKey, 'isAcUpdated', prevEntry.isAcUpdated, newEntry.isAcUpdated)

    return acc
  }, {} as UpdaterData)

  // return !Object.keys(diff).length ? null : diff
  return diff
}

function mergeDiff(diff: UpdaterData, key: string, patch: UpdaterData[string]) {
  diff[key] = { ...diff[key], ...patch }
}

function setFieldIfChanged<K extends keyof UpdaterData[string]>(
  diff: UpdaterData,
  key: string,
  field: K,
  prevValue: UpdaterData[string][K],
  nextValue: UpdaterData[string][K],
) {
  if (prevValue !== nextValue) {
    mergeDiff(diff, key, { [field]: nextValue } as UpdaterData[string])
  }
}

function areValuesIdentical(value1: unknown, value2: unknown): boolean {
  if (Array.isArray(value2)) {
    return areArraysIdentical(value1, value2)
  }

  if (isRecord(value1) && isRecord(value2)) {
    return areRecordsIdentical(value1, value2)
  }

  return value1 === value2
}

function areArraysIdentical(arr1: unknown, arr2: unknown[]): boolean {
  if (!Array.isArray(arr1)) {
    return false
  }

  if (arr1.length !== arr2.length) {
    return false
  }

  for (let i = 0; i < arr1.length; i++) {
    const left = arr1[i]
    const right = arr2[i]

    if (!isRecord(left) || !isRecord(right)) {
      return false
    }

    if (left.id !== right.id) {
      return false
    }
  }

  return true
}

function areRecordsIdentical(record1: Record<string, unknown>, record2: Record<string, unknown>): boolean {
  const record1Keys = Object.keys(record1)
  const record2Keys = Object.keys(record2)
  if (record1Keys.length !== record2Keys.length) {
    return false
  }
  for (const key of record1Keys) {
    if (!(key in record2)) {
      return false
    }
    if (!areValuesIdentical(record1[key], record2[key])) {
      return false
    }
  }
  return true
}

function findById(source: ActionCardData): UpdaterData {
  const result: UpdaterData = {}

  const visit = (value: unknown) => {
    if (!isRecord(value)) {
      return
    }

    if ('id' in value && isAppropriateObject(value)) {
      const isValidValue =
        typeof value.value === 'number' ||
        typeof value.value === 'string' ||
        typeof value.value === 'boolean' ||
        Array.isArray(value.value) ||
        isSignatureData(value.value) ||
        (value.type === WidgetType.AiTeammate && isRecord(value.value))

      const key = String(value.id)

      result[key] = {
        isOpened: !!value.isOpened,
        isLocked: !!value.isLocked,
        isSectionMinimized: !!value.isSectionMinimized,
        ...(value.hasFreeText ? { freeTextValue: value.freeTextValue as string | undefined } : {}),
        ...(isValidValue ? { value: value.value as never } : { checked: !!value.checked }),
        states: value.states as ObjectState[] | undefined,
        updatedAt: value.updatedAt as number | undefined,
        isAcUpdated: value.isAcUpdated as boolean | undefined,
      }
    }

    for (const next of Object.values(value)) {
      if (next && typeof next === 'object') {
        visit(next)
      }
    }
  }

  visit(source)

  return result
}

function isRecord(value: unknown): value is Record<string, any> {
  return typeof value === 'object' && value !== null
}

function isAppropriateObject(obj: Record<string, any>) {
  return (
    Object.hasOwn(obj, 'question') ||
    Object.hasOwn(obj, 'answer') ||
    Object.hasOwn(obj, 'text') ||
    obj.type === sectionTypes.SEPARATOR ||
    obj.type === WidgetType.GenericAiPrompt ||
    obj.type === WidgetType.UpdateWithAi ||
    obj.type === WidgetType.ConnectToCrm ||
    obj.type === WidgetType.AiTeammate ||
    Object.hasOwn(obj, 'states') ||
    Object.hasOwn(obj, 'updatedAt')
  )
}

function isSignatureData(value: unknown): value is SignatureData {
  return (
    typeof value === 'object' &&
    value !== null &&
    'viewBox' in value &&
    'points' in value &&
    typeof (value as SignatureData).viewBox === 'string' &&
    Array.isArray((value as SignatureData).points)
  )
}

export default getDifference
